import React from 'react'

import { useAppSelector } from 'app-redux/hooks'

import SaveService from 'services/save.service'

import { FORMAT } from 'types/deck'

import Icon from 'components/elements/Icon'
import CursorCardWrapper from 'components/card/CursorCardWrapper'
import DraggableBasicCard from 'components/card/DraggableBasicCard'
import ColorTagCssDropdown from 'components/card/controlsAndInfo/colorTagCssDropdown'
import DeckCardWrapper, { DeckCardWrapperChildProps } from 'components/card/deckCards/deckCardWrapper'
import ContextMenu from 'components/elements/ContextMenu'
import { cardManaCostToElement } from 'components/misc/customIcons/ManaSymbol/helpers'
import { setCodeToElement } from 'components/misc/customIcons/SetSymbols'
import { generateContextOptions } from 'components/card/deckCards/deckCardContextMenu'
import FoilSquare from 'components/card/controlsAndInfo/foilSquare'
import CollectionDot from 'components/card/controlsAndInfo/collectionDot'
import PinnedIndicator from 'components/card/controlsAndInfo/pinnedIndicator'

import { getPriceStringAndUrl } from 'utils/Price'

import styles from './textViewCard.module.scss'

type Props = {
  cardId: string
  stackName?: string
}

const TextViewCard = ({ cardId, stackName }: Props) => (
  <DeckCardWrapper cardId={cardId} stackName={stackName} component={Card} />
)

export default TextViewCard

const Card = ({ card, stackName, localCardData, deckData, userData, actions }: DeckCardWrapperChildProps) => {
  const textColumns = useAppSelector(state => state.cacheables.textColumns)
  const priceSource = useAppSelector(state => state.active.priceSource)
  const multiSelectedIds = useAppSelector(state => state.active.multiSelectedIds)
  const isMobile = useAppSelector(state => state.active.isMobile)
  const deckFormat = useAppSelector(state => state.deck.format)
  const disableDeckCursorCard = useAppSelector(state => state.cacheables.disableDeckCursorCard)

  // prettier-ignore
  const cardType = `${card.superTypes.join(' ')} ${card.types.join(' ')} ${card.subTypes.length > 0 ? ' - ' + card.subTypes.join(' ') : ''}`
  const canViewDragHandles = localCardData.ownsDeck && !isMobile
  const priceObj = getPriceStringAndUrl(card, priceSource[0], card.modifier !== 'Normal')

  return (
    <CursorCardWrapper
      disabled={disableDeckCursorCard}
      hoverDelay={65}
      className={`
      ${styles.card}
      ${localCardData.cardError && styles.cardError}
      ${localCardData.isMultiSelected && styles.selected}
      ${localCardData.antiHighlight && styles.antiHighlight}
    `}
      skipOnTouch
      flipped={localCardData.flipped}
      card={card}>
      <span className={`${styles.leftContent} ${localCardData.inSecondaryCategory && styles.dimmed}`}>
        {textColumns.dragHandle && canViewDragHandles && (
          <DraggableBasicCard
            card={card}
            dragItem={{
              card,
              stackName,
              multiSelectedIds: localCardData.isMultiSelected ? multiSelectedIds : undefined,
            }}>
            <span className={styles.dragHandle}>
              <Icon name="bars" />
            </span>
          </DraggableBasicCard>
        )}
        <button
          title={`${card.name} ${card.displayName ? `(${card.displayName})` : ''}`}
          className={styles.button}
          onClick={actions.cardClick}
          onContextMenu={actions.rightClick}>
          {localCardData.quantity}
          {card.companion && <Icon title="Companion" name="paw" />}
          <span className={styles.cardName}>
            <span>{card.name}</span>
            {card.displayName && (
              <span className={`${styles.displayName} ${card.lang === 'ph' ? styles.phyrexian : ''}`}>
                ({card.displayName})
              </span>
            )}
          </span>
        </button>
      </span>

      <span className={styles.rightContent}>
        {textColumns.type && cardType}
        {textColumns.finishes && <FoilSquare card={card} />}
        {deckFormat === FORMAT.EDH && textColumns.gamechangers && (
          <span>{card.gameChanger && <Icon name="gem outline" />}</span>
        )}

        {/* Always show shit regardless */}
        {!!card.customCard && (
          <span title="User custom card. (Not a real MTG card)">
            <Icon name="file image outline" />
          </span>
        )}

        {deckFormat === FORMAT.CANLANDER && textColumns.canlanderPoints && (
          <span style={{ opacity: card.canlanderPoints ? 1 : 0.2 }}>{card.canlanderPoints || '0'}</span>
        )}
        {textColumns.manaCost && cardManaCostToElement(card, styles.manaCost)}
        {textColumns.setSymbol && setCodeToElement(card.setCode, card.rarity, styles.setSymbol)}
        {textColumns.price && (
          <a href={priceObj.url} className={styles.shoppingUrl} rel="noopener noreferrer" target="_blank">
            {priceObj.price}
          </a>
        )}

        {textColumns.setName && <span>{card.set}</span>}
        {textColumns.collectionInfo && <CollectionDot card={card} />}

        {textColumns.colorTag && (
          <ColorTagCssDropdown
            card={card}
            onCardChange={updatedCard => SaveService.save(updatedCard)}
            disabled={localCardData.anyMultiSelected}
          />
        )}

        {textColumns.pinnedInfo && <PinnedIndicator card={card} onAddToPins={actions.addToArchidektPins} />}

        {textColumns.extraOptions && (
          <ContextMenu
            openOnLeftClick
            triggerClassName={localCardData.anyMultiSelected ? styles.hideDropdown : undefined}
            menuClassName={styles.dropdown}
            options={generateContextOptions({
              card,
              localCardData,
              actions,
              stackName,
              deckData,
              extraData: {},
              userData,
            })}>
            <button className={styles.extraOptionsTrigger}>
              <Icon name="ellipsis vertical" />
            </button>
          </ContextMenu>
        )}
      </span>
    </CursorCardWrapper>
  )
}
